import { createTheme } from '@mui/material';

/**
 * Field density tokens — compact, consistent sizing applied globally to MUI form
 * inputs (text fields, selects, autocompletes, pickers). Single source of truth:
 * lower FIELD_INPUT_PADDING_Y to shrink field height further; both are px.
 */
const FIELD_FONT_SIZE = '12px';
// Vertical padding inside plain inputs/selects → drives their height. Tuned so plain
// TextField/Select land at ~37px, matching MUI Autocomplete's (chips/tags) intrinsic
// height so all field types line up. Lower this to shrink every plain field at once.
const FIELD_INPUT_PADDING_Y = 8.5;
const FIELD_INPUT_PADDING_X = 10; // horizontal padding inside inputs

export const lightTheme = createTheme({
  typography: {
    fontSize: 13,
    fontFamily: ['Inter', 'Roboto', 'Arial'].join(','),
  },
  palette: {
    primary: {
      main: '#25849D',
    },
    action: {
      active: 'rgba(115, 198, 220, 1)',
      hover: 'rgba(0, 0, 0, 0.04)',
      hoverOpacity: 0.04,
      selected: 'rgba(0, 0, 0, 0.08)',
      selectedOpacity: 0.08,
    },
    background: {
      default: '#f1f5f9',
    },
    v4: {
      background: '#f1f5f9',
      surface: '#ffffff',
      border: '#e2e8f0',
      borderDark: '#cbd5e1',
      accent: '#0d9488',
      // `accent` is 3.75:1 on white — fine for borders and checkbox fills (3:1
      // non-text threshold) but short of WCAG AA 4.5:1 for text. `accentDark` is
      // 5.47:1, so use it whenever the teal carries a label.
      accentDark: '#0f766e',
      sidebar: '#1e293b',
      sidebarActive: '#0f172a',
      sidebarText: '#cbd5e1',
      sidebarTextMuted: '#94a3b8',
      tableEven: '#f8fafc',
      tableHover: '#f1f5f9',
      tableSelected: '#c7d2fe',
      tableSelectedHover: '#a5b4fc',
      filterInput: '#e2e8f0',
      filterInputBorder: '#cbd5e1',
      // --- Filter-bar control surface ---
      // The toolbar band sits one step below the white cards around it, and
      // every control on it is white. Controls are then told apart by their
      // fill against the band rather than by border weight — which is what made
      // the old all-white strip read flat, and why dropdowns carried a grey fill
      // that the text inputs beside them did not.
      barSurface: '#f8fafc',
      controlSurface: '#ffffff',
      controlBorderHover: '#94a3b8',
      // Table-pill tones. A muted, slate-leaning fill with a dark label of the
      // same hue: saturated pastels read as candy in a dense table, and solid
      // dark fills outweighed the rows around them. Every pairing is >= 5.9:1
      // and `TablePill.tests` measures them on each run.
      pill: {
        neutral: { bg: '#d5dce5', fg: '#2f3a4a' },
        info: { bg: '#cbdce9', fg: '#17455f' },
        positive: { bg: '#cddfd2', fg: '#1e4a32' },
        warning: { bg: '#e8dcc2', fg: '#6b4a18' },
        negative: { bg: '#e9d2d2', fg: '#7c2b2b' },
      },
      // A ticked row in a filter dropdown. Slate, not teal: the colour cue is
      // carried by the ticked checkbox and the label weight, so the row itself
      // only has to separate from its neighbours. A saturated teal band did
      // that job twice over and read as a highlighter pen.
      rowSelected: '#e2e8f0',
      rowSelectedHover: '#cbd5e1',
      // Pick-task row highlight by picking progress (matched lightness):
      // orange = below Required, blue = met Required, green = met Recommended.
      pickBelowRequired: '#fdeadd',
      pickMetRequired: '#e3f2fd',
      pickMetRecommended: '#e6f4ea',
      // Red: no source locations found for the part type (nothing to pick from).
      pickNoSource: '#fdecea',
      // Muted text tones used across the pick-task screens: textMuted = captions /
      // secondary labels, textFaint = empty-state / placeholder text, textError =
      // inline error text (e.g. "No source locations found"). Both meet WCAG AA
      // (>=4.5:1) on every light surface down to the #f1f5f9 page background
      // (textMuted ~6.9:1, textFaint ~5.7:1), with textFaint kept visibly lighter.
      textMuted: '#475569',
      textFaint: '#556270',
      textError: '#b00020',
      // Toast surface. A dark card on a light app: the toast is the one thing
      // on screen that isn't part of the page, so it deliberately doesn't look
      // like the page. It reuses the sidebar's navy rather than introducing a
      // second dark material — `surface` is `sidebarActive` and `body` is
      // `sidebarTextMuted`. Every accent below clears WCAG AA against
      // `surface` (success 9.3:1, error 6.5:1, warning 10.7:1, info 7.0:1,
      // body 7.0:1, title 19.9:1) and `Toast.tests` measures them each run.
      toast: {
        surface: '#0f172a',
        surfaceRecessed: '#475569',
        border: '#1e293b',
        title: '#f8fafc',
        body: '#94a3b8',
        success: '#34d399',
        error: '#f87171',
        warning: '#fbbf24',
        info: '#60a5fa',
        track: 'rgba(148, 163, 184, 0.24)',
        // A white scrim, because the only surface it sits on is the dark card.
        hoverOverlay: 'rgba(255, 255, 255, 0.08)',
        shadow: '0 10px 30px rgba(15, 23, 42, 0.28)',
      },
    },
    divider: '#0000000f',
    success: {
      main: '#4CAF50',
      contrastText: '#ffff',
    },
  },
  zIndex: {
    // MUI's default is 1400, which is *below* the ad-hoc z-indexes several
    // components hard-code for their popovers (1500 in SummaryBar/TagsField,
    // EditableText, EditableSelect, EditableSerialSearch; 1600 in
    // SummaryBar/AllocationField). A toast that renders underneath the control
    // that triggered it is no use, so the toast layer sits above all of them.
    // Raise this rather than adding another hard-coded number at a call site.
    snackbar: 1700,
  },
  components: {
    // --- Global form-field density (font 12px + trimmed height). Everything below
    // is CSS/defaults only; no logic. Tune via the FIELD_* constants above. ---
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: FIELD_FONT_SIZE },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Only the small size (what the field atoms use) is tightened.
          '&.MuiInputBase-sizeSmall .MuiOutlinedInput-input': {
            paddingTop: FIELD_INPUT_PADDING_Y,
            paddingBottom: FIELD_INPUT_PADDING_Y,
            paddingLeft: FIELD_INPUT_PADDING_X,
            paddingRight: FIELD_INPUT_PADDING_X,
          },
          // Select's displayed value shares the input box — trim it to match.
          '&.MuiInputBase-sizeSmall .MuiSelect-select': {
            paddingTop: FIELD_INPUT_PADDING_Y,
            paddingBottom: FIELD_INPUT_PADDING_Y,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: FIELD_FONT_SIZE },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: FIELD_FONT_SIZE },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '&.Mui-focusVisible': { background: 'rgba(0, 0, 0, 0.08)' },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: '#eeeeee',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e293b',
        },
      },
    },
  },
});
